import { useState, useEffect, useRef, useCallback } from "react";
import api, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  StickyNote,
  Plus,
  Edit2,
  Trash2,
  Lock,
  Eye,
  Bold,
  Italic,
  ChevronRight,
  ChevronDown,
  Link2,
  ListTodo,
  GripVertical,
  ExternalLink,
  Check,
  X,
  Pin,
  PinOff,
  Grid3X3,
  MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { linkify } from "@/lib/linkify";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import ScorecardEditor from "./ScorecardEditor";
import { MapEditor, MapDisplay } from "./MapNote";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = `${getApiUrl()}/api`;

/** Render markdown-style bold/italic */
function RichText({ text }) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**"))
      return <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>;
    if (part.startsWith("*") && part.endsWith("*"))
      return <em key={i} className="italic">{part.slice(1, -1)}</em>;
    return <span key={i}>{linkify(part, `an-${i}`)}</span>;
  });
}

function SortableNoteItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-1 group/sort">
      <button
        {...attributes}
        {...listeners}
        className="mt-1.5 p-0.5 text-white/20 hover:text-white/50 cursor-grab active:cursor-grabbing shrink-0 opacity-0 group-hover/sort:opacity-100 transition-opacity"
      >
        <GripVertical className="w-3 h-3" />
      </button>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

/** Icon button that shows on achievement cards — shows note count */
export function AchievementNotesButton({ count, onClick, size = "default" }) {
  const isSmall = size === "sm";
  return (
    <Button
      variant="ghost"
      size={isSmall ? "sm" : "icon"}
      onClick={onClick}
      className={`relative ${isSmall ? "h-10 w-10 p-0" : "h-8 w-8"} ${count > 0 ? "text-amber-400" : "text-white/40"} hover:text-amber-300`}
      title={`${count} note${count !== 1 ? "s" : ""}`}
      data-testid="achievement-notes-btn"
    >
      <StickyNote className={isSmall ? "w-5 h-5" : "w-3.5 h-3.5"} />
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-black text-[9px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center">
          {count}
        </span>
      )}
    </Button>
  );
}

/** Expandable notes panel for an achievement */
export function AchievementNotesPanel({ achievementId, gameId, isOpen, onToggle, onPlannerChange }) {
  const [notes, setNotes] = useState([]);
  const [scorecards, setScorecards] = useState([]);
  const [expandedGrids, setExpandedGrids] = useState({});
  const [contentOrder, setContentOrder] = useState([]);
  const [optimisticOrder, setOptimisticOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addType, setAddType] = useState("note"); // note, checklist, link, map, grid
  const [editing, setEditing] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [showTemplates, setShowTemplates] = useState(false);
  const [mapEditorOpen, setMapEditorOpen] = useState(false);
  const [editingMap, setEditingMap] = useState(null);
  const [newGridName, setNewGridName] = useState("");
  const [gridSaving, setGridSaving] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [checklistItems, setChecklistItems] = useState([]);
  const [newItemText, setNewItemText] = useState("");
  const textareaRef = useRef(null);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const fetchNotes = useCallback(async () => {
    if (!achievementId) return;
    setLoading(true);
    try {
      const [notesRes, gridsRes, orderRes] = await Promise.all([
        api.get(`${API}/achievements/${achievementId}/notes`),
        gameId
          ? api.get(`${API}/scorecards/game/${gameId}?achievement_id=${achievementId}`)
          : Promise.resolve({ data: [] }),
        api.get(`${API}/achievements/${achievementId}/content/order`).catch(() => ({ data: { orderedIds: [] } })),
      ]);
      setNotes(Array.isArray(notesRes.data) ? notesRes.data : []);
      setScorecards(Array.isArray(gridsRes.data) ? gridsRes.data : []);
      setContentOrder((orderRes.data?.orderedIds) || []);
      setOptimisticOrder(null);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [achievementId, gameId]);

  useEffect(() => {
    if (isOpen) fetchNotes();
  }, [isOpen, fetchNotes]);

  const resetForm = () => {
    setTitle("");
    setText("");
    setIsPrivate(false);
    setLinkUrl("");
    setChecklistItems([]);
    setNewItemText("");
    setAdding(false);
    setEditing(null);
    setAddType("note");
    setNewGridName("");
    setShowTemplates(false);
  };

  const openEdit = (note) => {
    setEditing(note);
    setAddType(note.noteType || "note");
    setTitle(note.title || "");
    setText(note.text || "");
    setIsPrivate(note.isPrivate || false);
    setLinkUrl(note.url || "");
    setChecklistItems(note.checklistItems || []);
    setAdding(true);
  };

  const handleSave = async () => {
    if (addType === "link" && !linkUrl.trim()) {
      toast.error("URL is required for links");
      return;
    }
    if (!text.trim() && addType !== "link") {
      toast.error("Text is required");
      return;
    }

    try {
      const payload = {
        text: text.trim() || linkUrl.trim(),
        title: title.trim() || null,
        noteType: addType,
        isPrivate,
        ...(addType === "link" && { url: linkUrl.trim() }),
        ...(addType === "checklist" && { checklistItems }),
      };

      if (editing) {
        await api.put(`${API}/achievements/${achievementId}/notes/${editing.id}`, payload);
        toast.success("Note updated");
      } else {
        await api.post(`${API}/achievements/${achievementId}/notes`, payload);
        toast.success("Note added");
      }
      resetForm();
      fetchNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleSaveMap = async (mapData) => {
    try {
      if (editingMap) {
        await api.put(`${API}/achievements/${achievementId}/notes/${editingMap.id}`, mapData);
        toast.success("Map updated");
      } else {
        await api.post(`${API}/achievements/${achievementId}/notes`, mapData);
        toast.success("Map added");
      }
      setMapEditorOpen(false);
      setEditingMap(null);
      fetchNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save map");
    }
  };

  const handleToggleMapPin = async (noteId, pinId) => {
    try {
      await api.patch(`${API}/achievements/${achievementId}/notes/${noteId}/pins/${pinId}`, {});
      fetchNotes();
    } catch (err) {
      toast.error("Failed to toggle pin");
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this note?\n\nDeleted notes cannot be recovered.")) return;
    try {
      await api.delete(`${API}/achievements/${achievementId}/notes/${noteId}`);
      toast.success("Note deleted");
      fetchNotes();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleToggleChecklist = async (noteId, itemIndex) => {
    try {
      await api.patch(`${API}/achievements/${achievementId}/notes/${noteId}/checklist/${itemIndex}`);
      fetchNotes();
    } catch {
      toast.error("Failed to toggle item");
    }
  };

  const handleReorder = async (orderedIds) => {
    setOptimisticOrder(orderedIds);
    try {
      await api.put(`${API}/achievements/${achievementId}/content/reorder`, {
        orderedIds,
      });
      setContentOrder(orderedIds);
    } catch {
      toast.error("Failed to reorder");
      setOptimisticOrder(null);
    }
  };

  // --- Grid (scorecard) CRUD inside the unified list ---
  const createGrid = async (fromTemplateId = null) => {
    if (!gameId) {
      toast.error("Grids require a game context");
      return;
    }
    setGridSaving(true);
    try {
      const res = await api.post(`${API}/scorecards`, {
        name: newGridName || "Grid",
        gameId,
        achievementId,
        fromTemplateId,
      });
      setScorecards(prev => [...prev, res.data]);
      setExpandedGrids(prev => ({ ...prev, [res.data.id]: true }));
      setNewGridName("");
      setAdding(false);
      setAddType("note");
      toast.success("Grid created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create grid");
    } finally {
      setGridSaving(false);
    }
  };

  const saveGrid = async (scorecardId, data) => {
    try {
      const res = await api.put(`${API}/scorecards/${scorecardId}`, data);
      setScorecards(prev => prev.map(s => s.id === scorecardId ? res.data : s));
      toast.success("Grid saved");
    } catch {
      toast.error("Failed to save grid");
    }
  };

  const deleteGrid = async (scorecardId, name) => {
    if (!window.confirm(`Are you sure you want to delete this grid${name ? ` "${name}"` : ""}?\n\nDeleted grids cannot be recovered.`)) return;
    try {
      await api.delete(`${API}/scorecards/${scorecardId}`);
      setScorecards(prev => prev.filter(s => s.id !== scorecardId));
      toast.success("Grid deleted");
    } catch {
      toast.error("Failed to delete grid");
    }
  };

  const handleTogglePlanner = async (noteId) => {
    try {
      const { data } = await api.patch(`${API}/achievements/notes/${noteId}/toggle-planner`);
      toast.success(data.pinnedToPlanner ? "Added to planner" : "Removed from planner");
      fetchNotes();
      if (onPlannerChange) onPlannerChange();
    } catch {
      toast.error("Failed to toggle planner pin");
    }
  };

  const wrapSelection = (wrapper) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = text.substring(start, end);
    const before = text.substring(0, start);
    const after = text.substring(end);
    setText(sel ? before + wrapper + sel + wrapper + after : before + wrapper + wrapper + after);
  };

  if (!isOpen) return null;

  // Build the unified mixed-type list (notes + grids) ordered by contentOrder.
  // Items not in contentOrder are appended in their natural order so newly
  // created items still appear without a manual reorder.
  const buildCombinedItems = () => {
    const taggedNotes = notes.map(n => ({ ...n, __kind: "note", __id: n.id }));
    const taggedGrids = scorecards.map(s => ({ ...s, __kind: "grid", __id: s.id }));
    const all = [...taggedNotes, ...taggedGrids];
    const order = optimisticOrder || contentOrder;
    if (!order || order.length === 0) return all;
    const byId = new Map(all.map(it => [it.__id, it]));
    const ordered = [];
    const seen = new Set();
    for (const id of order) {
      const it = byId.get(id);
      if (it) { ordered.push(it); seen.add(id); }
    }
    for (const it of all) {
      if (!seen.has(it.__id)) ordered.push(it);
    }
    return ordered;
  };
  const displayItems = buildCombinedItems();

  return (
    <div className="mt-2 pt-2 border-t border-white/5 space-y-2" data-testid="achievement-notes-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-white/50 text-xs flex items-center gap-1">
          <StickyNote className="w-3 h-3" /> Achievement Notes ({notes.length + scorecards.length})
        </span>
        {!adding && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[10px] text-amber-400 hover:text-amber-300"
            onClick={() => { resetForm(); setAdding(true); }}
            data-testid="add-achievement-note-btn"
          >
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        )}
      </div>

      {/* Add/Edit form */}
      {adding && (
        <div className="bg-black/30 rounded-md p-2.5 space-y-2 border border-white/10" data-testid="achievement-note-form">
          {/* Type tabs */}
          <div className="flex gap-1 flex-wrap">
            {[
              { key: "note", icon: StickyNote, label: "Note" },
              { key: "checklist", icon: ListTodo, label: "List" },
              { key: "link", icon: Link2, label: "Link" },
              { key: "map", icon: MapPin, label: "Map" },
              { key: "grid", icon: Grid3X3, label: "Grid" },
            ].map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                onClick={() => {
                  if (key === "map") {
                    setMapEditorOpen(true);
                    setEditingMap(null);
                    return;
                  }
                  setAddType(key);
                }}
                className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-colors ${addType === key ? "bg-white/10 text-white" : "text-white/40 hover:text-white/60"}`}
                data-testid={`ach-note-tab-${key}`}
              >
                <Icon className="w-3 h-3" /> {label}
              </button>
            ))}
          </div>

          {/* Title (not applicable to grids — grid creation only needs a name) */}
          {addType !== "grid" && (
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title (optional)"
              className="h-7 text-xs bg-black/50 border-white/10"
              data-testid="ach-note-title"
            />
          )}
          {addType === "note" && (
            <>
              <div className="flex items-center gap-1">
                <button onClick={() => wrapSelection("**")} className="p-1 text-white/30 hover:text-white/60" title="Bold"><Bold className="w-3 h-3" /></button>
                <button onClick={() => wrapSelection("*")} className="p-1 text-white/30 hover:text-white/60" title="Italic"><Italic className="w-3 h-3" /></button>
              </div>
              <Textarea
                ref={textareaRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Note text..."
                className="min-h-[60px] text-xs bg-black/50 border-white/10"
                data-testid="ach-note-text"
              />
            </>
          )}

          {addType === "link" && (
            <>
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Link title..."
                className="h-7 text-xs bg-black/50 border-white/10"
              />
              <Input
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://..."
                className="h-7 text-xs bg-black/50 border-white/10"
                data-testid="ach-note-url"
              />
            </>
          )}

          {addType === "checklist" && (
            <>
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Checklist name..."
                className="h-7 text-xs bg-black/50 border-white/10"
              />
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {checklistItems.map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-xs text-white/70">
                    <span className="w-3 h-3 border border-white/20 rounded-sm shrink-0" />
                    <span className="flex-1">{item.text || item}</span>
                    <button onClick={() => setChecklistItems(prev => prev.filter((_, j) => j !== i))} className="text-red-400/40 hover:text-red-400">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-1">
                <Input
                  value={newItemText}
                  onChange={(e) => setNewItemText(e.target.value)}
                  placeholder="Add item..."
                  className="h-6 text-[10px] bg-black/50 border-white/10"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newItemText.trim()) {
                      setChecklistItems(prev => [...prev, { text: newItemText.trim(), checked: false }]);
                      setNewItemText("");
                    }
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-1.5"
                  onClick={() => {
                    if (newItemText.trim()) {
                      setChecklistItems(prev => [...prev, { text: newItemText.trim(), checked: false }]);
                      setNewItemText("");
                    }
                  }}
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </>
          )}

          {addType === "grid" && (
            <div className="space-y-1.5">
              <Input
                value={newGridName}
                onChange={(e) => setNewGridName(e.target.value)}
                placeholder="Grid name (e.g., Score Tracker)"
                className="h-7 text-xs bg-black/50 border-white/10"
                onKeyDown={(e) => e.key === "Enter" && createGrid()}
                data-testid="ach-grid-name"
              />
              <p className="text-white/30 text-[10px]">
                A blank grid will be created. For templates and sharing, open the game-level grids.
              </p>
            </div>
          )}

          {/* Private + actions row */}
          <div className="flex items-center justify-between pt-1">
            {addType !== "grid" ? (
              <button
                type="button"
                onClick={() => setIsPrivate(!isPrivate)}
                className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded border transition-colors ${isPrivate ? "bg-red-500/10 border-red-500/30 text-red-400" : "bg-white/5 border-white/10 text-white/40"}`}
                data-testid="ach-note-private"
              >
                {isPrivate ? <Lock className="w-2.5 h-2.5" /> : <Eye className="w-2.5 h-2.5" />}
                {isPrivate ? "Private" : "Shared"}
              </button>
            ) : <span />}
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-white/40" onClick={resetForm}>Cancel</Button>
              {addType === "grid" ? (
                <Button
                  size="sm"
                  className="h-6 px-2 text-[10px] bg-emerald-500 hover:bg-emerald-600 text-black"
                  onClick={() => createGrid()}
                  disabled={gridSaving}
                  data-testid="ach-grid-save"
                >
                  Create
                </Button>
              ) : (
                <Button size="sm" className="h-6 px-2 text-[10px] bg-amber-500 hover:bg-amber-600 text-black" onClick={handleSave} data-testid="ach-note-save">
                  {editing ? "Update" : "Save"}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Unified list — notes + grids share a single DnD context so any
          type can be reordered above any other. */}
      {loading ? (
        <p className="text-white/30 text-xs text-center py-2">Loading...</p>
      ) : displayItems.length === 0 && !adding ? (
        <p className="text-white/20 text-[10px] text-center py-1">No notes or grids on this achievement yet</p>
      ) : (
        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={(event) => {
          const { active, over } = event;
          if (!over || active.id === over.id) return;
          const ids = displayItems.map(it => it.__id);
          const oldIdx = ids.indexOf(active.id);
          const newIdx = ids.indexOf(over.id);
          if (oldIdx !== -1 && newIdx !== -1) {
            handleReorder(arrayMove(ids, oldIdx, newIdx));
          }
        }}>
          <SortableContext items={displayItems.map(it => it.__id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-1.5">
              {displayItems.map((item) => {
                if (item.__kind === "grid") {
                  const sc = item;
                  return (
                    <SortableNoteItem key={`grid-${sc.id}`} id={sc.id}>
                      <Card className="bg-black/20 border-white/5 hover:border-emerald-500/30 transition-colors group/note rounded-xl" data-testid={`ach-grid-card-${sc.id}`}>
                        <CardContent className="p-0">
                          <div className="flex items-center gap-1.5 px-2 py-1.5">
                            <button
                              onClick={() => setExpandedGrids(prev => ({ ...prev, [sc.id]: !prev[sc.id] }))}
                              className="text-white/40 hover:text-white"
                            >
                              {expandedGrids[sc.id]
                                ? <ChevronDown className="w-3.5 h-3.5" />
                                : <ChevronRight className="w-3.5 h-3.5" />}
                            </button>
                            <Grid3X3 className="w-3 h-3 text-emerald-400 shrink-0" />
                            <span className="text-xs text-white font-medium flex-1 truncate">{sc.name}</span>
                            <span className="text-white/20 text-[10px]">{sc.rows?.length || 0}x{sc.cols?.length || 0}</span>
                            <button
                              onClick={() => deleteGrid(sc.id, sc.name)}
                              className="opacity-0 group-hover/note:opacity-100 text-white/30 hover:text-red-400 p-0.5 transition-opacity"
                              data-testid={`delete-ach-grid-${sc.id}`}
                              title="Delete grid"
                            >
                              <Trash2 className="w-2.5 h-2.5" />
                            </button>
                          </div>
                          {expandedGrids[sc.id] && (
                            <div className="px-2 py-2 border-t border-white/5">
                              <ScorecardEditor scorecard={sc} onSave={(data) => saveGrid(sc.id, data)} />
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </SortableNoteItem>
                  );
                }

                const note = item;
                return (
                <SortableNoteItem key={`note-${note.id}`} id={note.id}>
                  {note.noteType === "map" ? (
                    <MapDisplay
                      note={note}
                      onTogglePin={(noteId, pinId) => handleToggleMapPin(noteId, pinId)}
                      onEdit={(n) => { setEditingMap(n); setMapEditorOpen(true); }}
                      onDelete={handleDelete}
                    />
                  ) : (
                  <div className="bg-black/20 rounded-xl p-2 border border-white/5 group/note">
                    <div className="flex items-start gap-1.5">
                      {note.noteType === "link" ? (
                        <Link2 className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />
                      ) : note.noteType === "checklist" ? (
                        <ListTodo className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />
                      ) : (
                        <StickyNote className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        {/* Title row */}
                        <div className="flex items-center gap-1">
                          {note.title && (
                            <button
                              onClick={() => setExpandedIds(prev => {
                                const next = new Set(prev);
                                next.has(note.id) ? next.delete(note.id) : next.add(note.id);
                                return next;
                              })}
                              className="flex items-center gap-0.5 text-white/80 text-xs font-medium hover:text-amber-300"
                            >
                              <ChevronRight className={`w-3 h-3 transition-transform ${expandedIds.has(note.id) ? "rotate-90" : ""}`} />
                              {note.title}
                            </button>
                          )}
                          {note.isPrivate && <Lock className="w-2.5 h-2.5 text-red-400/50 shrink-0" />}
                          {note.pinnedToPlanner && <Pin className="w-2.5 h-2.5 text-cyan-400/50 shrink-0" />}
                          <div className="ml-auto flex gap-0.5 opacity-0 group-hover/note:opacity-100 transition-opacity">
                            <button onClick={() => handleTogglePlanner(note.id)} className={`p-0.5 ${note.pinnedToPlanner ? "text-cyan-400" : "text-white/30 hover:text-cyan-400"}`} title={note.pinnedToPlanner ? "Unpin from planner" : "Pin to planner"}>
                              {note.pinnedToPlanner ? <PinOff className="w-2.5 h-2.5" /> : <Pin className="w-2.5 h-2.5" />}
                            </button>
                            <button onClick={() => openEdit(note)} className="text-white/30 hover:text-amber-400 p-0.5">
                              <Edit2 className="w-2.5 h-2.5" />
                            </button>
                            <button onClick={() => handleDelete(note.id)} className="text-white/30 hover:text-red-400 p-0.5">
                              <Trash2 className="w-2.5 h-2.5" />
                            </button>
                          </div>
                        </div>

                        {/* Content */}
                        {(!note.title || expandedIds.has(note.id)) && (
                          <>
                            {note.noteType === "link" ? (
                              <a href={note.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1 mt-0.5">
                                <ExternalLink className="w-3 h-3 shrink-0" />
                                <span className="truncate">{note.text}</span>
                              </a>
                            ) : note.noteType === "checklist" ? (
                              <div className="space-y-1 mt-1">
                                {(note.checklistItems || []).map((it, i) => (
                                  <label key={i} className="flex items-center gap-1.5 text-xs cursor-pointer">
                                    <Checkbox
                                      checked={it.checked}
                                      onCheckedChange={() => handleToggleChecklist(note.id, i)}
                                      className="w-3 h-3"
                                    />
                                    <span className={it.checked ? "line-through text-white/30" : "text-white/70"}>{it.text}</span>
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <div className="text-white/60 text-xs mt-0.5 whitespace-pre-wrap break-words">
                                <RichText text={note.text} />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  )}
                </SortableNoteItem>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Map Editor Dialog */}
      {mapEditorOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => { setMapEditorOpen(false); setEditingMap(null); }}>
          <div className="bg-[#0A0A0F] border border-white/10 rounded-xl p-4 w-full max-w-2xl max-h-[85vh] overflow-y-auto mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-medium text-sm mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-emerald-400" />
              {editingMap ? "Edit Map" : "Create Map"}
            </h3>
            <MapEditor
              initialData={editingMap}
              API={API}
              gameId={gameId}
              onSave={handleSaveMap}
              onCancel={() => { setMapEditorOpen(false); setEditingMap(null); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
